/**
 * Relative Time Formatters
 *
 * Locale-aware relative time formatting using Intl.RelativeTimeFormat.
 */

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type TimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

interface TimeUnitConfig {
  unit: TimeUnit;
  seconds: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const TIME_UNITS: TimeUnitConfig[] = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function toDate(date: Date | string | number): Date {
  if (date instanceof Date) return date;
  return new Date(date);
}

function getRelativeTimeUnit(diffInSeconds: number): { value: number; unit: TimeUnit } {
  const absDiff = Math.abs(diffInSeconds);

  for (const { unit, seconds } of TIME_UNITS) {
    if (absDiff >= seconds) {
      const value = Math.round(diffInSeconds / seconds);
      return { value, unit };
    }
  }

  return { value: Math.round(diffInSeconds), unit: 'second' };
}

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format a date as relative time (e.g., "2 hours ago", "in 3 days")
 *
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'en-US', 'de-DE')
 * @param baseDate - Reference date (defaults to now)
 *
 * @example
 * formatRelativeTime(twoHoursAgo, 'en-US') // "2 hours ago"
 * formatRelativeTime(nextWeek, 'de-DE') // "in 1 Woche"
 */
export function formatRelativeTime(
  date: Date | string | number,
  locale: string,
  baseDate: Date = new Date(),
  /** `'short'` ("5 min. ago") for dense surfaces such as pipeline cards. */
  style: Intl.RelativeTimeFormatStyle = 'long'
): string {
  try {
    const d = toDate(date);
    const diffInSeconds = (d.getTime() - baseDate.getTime()) / 1000;
    const { value, unit } = getRelativeTimeUnit(diffInSeconds);

    const formatter = new Intl.RelativeTimeFormat(locale, {
      numeric: 'auto',
      style,
    });

    return formatter.format(value, unit);
  } catch {
    return String(date);
  }
}

/**
 * Format a date as time ago (always past tense)
 *
 * @param date - Date to format
 * @param locale - Locale string
 *
 * @example
 * formatTimeAgo(twoHoursAgo, 'en-US') // "2 hours ago"
 * formatTimeAgo(justNow, 'en-US') // "just now"
 */
export function formatTimeAgo(date: Date | string | number, locale: string): string {
  try {
    const d = toDate(date);
    const now = new Date();
    const diffInSeconds = (now.getTime() - d.getTime()) / 1000;

    // Handle future dates by treating them as "just now"
    if (diffInSeconds < 0) {
      return formatRelativeTime(now, locale, now);
    }

    // Very recent (less than 10 seconds)
    if (diffInSeconds < 10) {
      const formatter = new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
        style: 'long',
      });
      return formatter.format(0, 'second'); // "now" / "just now"
    }

    const { value, unit } = getRelativeTimeUnit(-diffInSeconds);

    const formatter = new Intl.RelativeTimeFormat(locale, {
      numeric: 'always',
      style: 'long',
    });

    return formatter.format(value, unit);
  } catch {
    return String(date);
  }
}
